import React, { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import theme from 'stories/utils/theme'
import { FloatingBannerSlice } from 'interfaces/GeneratedLandingPage'
import { ButtonText, Headline5 } from 'stories/elements/Typography/Text'
import Link from 'next/link'
import DynamicButton from './DynamicButton'

const BannerWrapperDesktop = styled.div<{
    backgroundColor?: string
    leaveSpaceForWhatsAppBanner?: boolean
    isScrolledDown?: boolean
    navbarColor?: string
    showOnMobile?: boolean
}>`
    position: fixed;
    background: ${({ backgroundColor }) => backgroundColor ?? theme.colors.knowunityBlue};
    bottom: 0;
    padding: 0 25px;
    width: 100%;
    height: 80px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    z-index: ${theme.zIndex.topBanner};
    transform: translateY(80px);
    will-change: transform;
    h5 {
        width: 100%;
    }
    .buttons {
        display: flex;
        gap: 20px;
        align-items: center;
        justify-content: end;
        padding-right: ${({ leaveSpaceForWhatsAppBanner }) => (leaveSpaceForWhatsAppBanner ? 325 : 0)}px;
    }

    transition: transform 0.5s;
    animation: ${({ isScrolledDown }) => (isScrolledDown ? 'change 0.5s 1' : 'none')};
    animation-play-state: ${({ isScrolledDown }) => (isScrolledDown ? 'running' : 'paused')};
    animation-fill-mode: ${({ isScrolledDown }) => (isScrolledDown ? 'normal' : 'none')};
    animation-timing-function: ease-in-out;
    @keyframes change {
        0% {
            background-color: transparent;
        }

        75% {
            background-color: ${({ navbarColor }) => navbarColor ?? theme.colors.deepSeaBlue};
        }
    }
    ${({ isScrolledDown }) =>
        isScrolledDown
            ? css`
                  transform: translateY(calc(-0px));
              `
            : ''}
    ${({ showOnMobile }) =>
        !showOnMobile
            ? css`
                  @media (max-width: 750px) {
                      display: none;
                  }
              `
            : ''}
`

interface Props {
    data: FloatingBannerSlice['primary']
    onPrimaryButtonClicked?: () => void
    showWithoutScrolling?: boolean
    showOnMobile?: boolean
}

const FloatingBanner = ({ data, onPrimaryButtonClicked, showWithoutScrolling, showOnMobile }: Props) => {
    const [scrollPosition, setScrollPosition] = useState(0)

    useEffect(() => {
        window.addEventListener('scroll', listenToScroll)
        listenToScroll()
        return () => {
            window.removeEventListener('scroll', listenToScroll)
        }
    }, [])

    const listenToScroll = () => {
        setScrollPosition(document.body.scrollTop || document.documentElement.scrollTop)
    }

    const isScrolledDown = showWithoutScrolling || scrollPosition > 0

    const buttonsAreMissing = !data.primary_button_link && !data.secondary_button_link

    return (
        <BannerWrapperDesktop
            backgroundColor={data.background_color}
            leaveSpaceForWhatsAppBanner={data.whatsapp_banner_present}
            navbarColor={data.background_color}
            isScrolledDown={isScrolledDown}
            showOnMobile={showOnMobile}
        >
            <Headline5 color={theme.colors.white} textAlign={buttonsAreMissing ? 'center' : 'left'}>
                {data.title}
            </Headline5>
            {!buttonsAreMissing ? (
                <div className="buttons">
                    <span onClick={onPrimaryButtonClicked}>
                        <DynamicButton
                            width={225}
                            height={40}
                            fullWidth
                            href={data.primary_button_link}
                            borderRadius={theme.borderRadius.normal}
                            color={data.primary_button_color ?? theme.colors.accentBlue}
                        >
                            <ButtonText color={theme.colors.white}>{data.primary_button_text}</ButtonText>
                        </DynamicButton>
                    </span>

                    {data.secondary_button_link ? (
                        <Link href={data.secondary_button_link} prefetch={false}>
                            <ButtonText color={theme.colors.white} textAlign="center">
                                {data.secondary_button_text}
                            </ButtonText>
                        </Link>
                    ) : null}
                </div>
            ) : null}
        </BannerWrapperDesktop>
    )
}

export default FloatingBanner
